/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import Moment from 'moment' // Npm: Moment library.
import PropTypes from 'prop-types' // Npm: react.js library.
import _ from 'underscore' // Npm: underscore library.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { useQuery } from '@apollo/client' // Npm: Apollo client.
import { IoMdAdd } from 'react-icons/io' // Npm: React icons.
import { HiArrowPath } from 'react-icons/hi2' // Npm: React icons.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Button,
  Flex,
  Tooltip,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import DownloadToExcel from 'components/DownloadToExcel'
import Modal from 'components/Modal'
import TableSpinner from 'components/TableSpinner'
import VendorAccounttDropDownOptions from 'components/VendorAccounttDropDownOptions'
import VendorAccounttUpsert from 'components/VendorAccounttUpsert'
import VendorAccounttDelete from 'components/VendorAccounttDelete'


/*
 * GRAPHS
 */
import VendorAccounttReadQuery from './__query__/index.vendorAccountt.read.query'


/*
 * STYLES
 */
import {
  buttonStyle
} from './index.style'


/*
 * OBJECTS
 */
const Index = ({ passOn }) => {
  // Const assignment.
  const _successFlags = React.useMemo(() => Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key), [])

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 10,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [filterModel, setFilterModel] = React.useState({ items: [] })
  const [vendorAccounttToSearch, setVendorAccounttToSearch] = React.useState('')
  const { 'isOpen': isVendorAccounttCreateOpen, 'onOpen': onVendorAccounttCreateOpen, 'onClose': onVendorAccounttCreateClose } = useDisclosure()
  const { 'isOpen': isVendorAccounttUpdateOpen, 'onOpen': onVendorAccounttUpdateOpen, 'onClose': onVendorAccounttUpdateClose } = useDisclosure()
  const { 'isOpen': isVendorAccounttDeleteOpen, 'onOpen': onVendorAccounttDeleteOpen, 'onClose': onVendorAccounttDeleteClose } = useDisclosure()
  const _isPreviousPageRef = React.useRef(false)
  const _paginationModelRef = React.useRef(paginationModel)
  const _rowCountRef = React.useRef(0)
  const _ResetPagination = React.useCallback(() => {
    _isPreviousPageRef.current = false
    setPaginationModel(j => {
      const nextPaginationModel = { ...j, 'page': 0 }

      _paginationModelRef.current = nextPaginationModel

      return nextPaginationModel
    })
  }, [])
  const _QueryVendorAccounttReadVariables = React.useMemo(() => ({
    vendorAccounttDirectoryId: passOn?.vendorAccounttDirectoryId,
    vendorAccounttToSearch,
    skip: paginationModel.pageSize * paginationModel.page,
    take: paginationModel.pageSize,
  }), [paginationModel.page, paginationModel.pageSize, passOn?.vendorAccounttDirectoryId, vendorAccounttToSearch])
  const _QueryVendorAccounttRead = useQuery(VendorAccounttReadQuery, {
    variables: _QueryVendorAccounttReadVariables,
    fetchPolicy: _isPreviousPageRef.current ? 'cache-first' : Object.React.App.fetchPolicy,
  });
  const _onVendorAccounttToSearchInputChange = React.useRef(
    _.debounce((value) => {
      _ResetPagination()
      setVendorAccounttToSearch(value)
    }, 800),
  ).current;
  const _HandlePaginationModelChange = React.useCallback(__paginationModel => {
    // Only backward pagination with the same page size should prefer cached rows.
    _isPreviousPageRef.current =
      __paginationModel.page < _paginationModelRef.current.page &&
      __paginationModel.pageSize === _paginationModelRef.current.pageSize

    _paginationModelRef.current = __paginationModel
    setPaginationModel(__paginationModel)
  }, [])
  const _HandleFilterModelChange = React.useCallback(__filterModel => {
    _ResetPagination()
    setFilterModel(__filterModel)
  }, [_ResetPagination])
  const _HandleColumnVisibilityModelChange = React.useCallback(newModel => {
    setColumnVisibilityModel(newModel)
  }, [])
  const _QueryVendorAccounttReadRowCount = _.pluck(
    _QueryVendorAccounttRead?.data?.VendorAccounttRead,
    '_totalCount'
  )?.[0]
  const rowCount = React.useMemo(() => {
    if (undefined !== _QueryVendorAccounttReadRowCount && null !== _QueryVendorAccounttReadRowCount) _rowCountRef.current = _QueryVendorAccounttReadRowCount

    return _rowCountRef.current
  }, [_QueryVendorAccounttReadRowCount])
  const _tableHeaderHeightRef = React.useRef(0);
  const _isCurrentViewMobile = useBreakpointValue({
    base: false,
    md: false,
    lg: false,
    xl: false,
    sm: true,
    xs: true,
  }) ?? false;

  // Extract search from filter model
  React.useEffect(() => {
    const displayNameFilter = filterModel?.items?.find(
      (item) => item.field === "displayName",
    );
    const searchValue = displayNameFilter?.value || "";
    _onVendorAccounttToSearchInputChange(searchValue);
  }, [_onVendorAccounttToSearchInputChange, filterModel]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      _onVendorAccounttToSearchInputChange.cancel?.();
    };
  }, []);

  // Event handler.
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const _HandleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);

    try {
      await _QueryVendorAccounttRead.refetch();
    } finally {
      setIsRefreshing(false);
    }
  }, [_QueryVendorAccounttRead.refetch]);

  // Data assignment.
  const columns = React.useMemo(() => [
    {
      'field': 'sno',
      'headerName': 'S.No.',
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 180
    },
    {
      'field': 'id',
      'headerName': 'ID',
      'align': 'center',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'headerAlign': 'center',
      'hide': true
    },
    {
      'field': 'displayName',
      'headerName': 'DisplayName',
      'description': 'Display name of vendor account.',
      'editable': false,
      'sortable': false,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'width': 300,
    },
    {
      'field': 'vendorAccountCount',
      'headerName': 'VendorAccount',
      'description': 'Number of vendor accounts.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 300,
    },
    {
      'field': 'createdAt',
      'headerName': 'Created At',
      'description': 'Creation date.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 250,
    },
    {
      'field': 'updatedAt',
      'headerName': 'Updated At',
      'description': 'Last update date.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 250,
    },
    {
      'field': 'action',
      'headerName': 'Actions',
      'description': 'Actions for vendor account.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 150,
      'disableColumnMenu': true,
      'renderCell': ({ row }) => <VendorAccounttDropDownOptions
        vendorAccounttId={row?.id}
        onVendorAccounttUpdate={onVendorAccounttUpdateOpen}
        onVendorAccounttDelete={onVendorAccounttDeleteOpen}
      />
    }
  ], [onVendorAccounttUpdateOpen, onVendorAccounttDeleteOpen])

  const rows = React.useMemo(() => _.compact(_QueryVendorAccounttRead?.data?.VendorAccounttRead?.map((r, index) => _successFlags.includes(r.status) ? ({
    'id': r?.id ?? `vendoraccountt-directory-item-${paginationModel.page}-${paginationModel.pageSize}-${index}`,
    'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
    'displayName': r?.displayName ?? '-',
    'vendorAccountCount': r?.VendorAccount?.length ?? '-',
    'createdAt': Moment(r?.createdAt).format('YYYY-MM-DD'),
    'updatedAt': Moment(r?.updatedAt).format('YYYY-MM-DD')
  }) : null)), [_QueryVendorAccounttRead?.data?.VendorAccounttRead, _successFlags, paginationModel.page, paginationModel.pageSize])

  // Return component.
  return (
    <>
      <Flex className="vendorAccountt base">
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          gap={_isCurrentViewMobile ? "12px" : "22px"}
          bg="white"
          minH="81vh"
          maxH="81vh"
          borderRadius="20px"
          p={_isCurrentViewMobile ? "12px" : "22px"}
        >
          <Flex
            pb="0px"
            justify="space-between"
            gap="12px"
            flexWrap={{ base: "wrap", md: "nowrap" }}
          >
            <Tooltip label="Add Vendor Account" fontSize="sm">
              <Button
                leftIcon={<IoMdAdd />}
                onClick={onVendorAccounttCreateOpen}
                style={buttonStyle}
              >
                Add Vendor A/C
              </Button>
            </Tooltip>
            <Flex gap="12px">
              <Button
                borderRadius="8px"
                fontWeight="450"
                minW="20px"
                backgroundColor="purple.200"
                color="purple"
                _hover={{ bg: "purple.800", color: "white" }}
                leftIcon={<HiArrowPath />}
                isDisabled={isRefreshing}
                onClick={_HandleRefresh}
              >
                Refresh
              </Button>
              <DownloadToExcel
                cellsData={
                  0 ===
                    _QueryVendorAccounttRead?.data?.VendorAccounttRead
                      ?.length ||
                  !_.every(
                    _.pluck(
                      _QueryVendorAccounttRead?.data?.VendorAccounttRead,
                      "status",
                    ),
                    (i) => _successFlags.includes(i),
                  )
                    ? []
                    : _QueryVendorAccounttRead?.data?.VendorAccounttRead.map(
                        (item, __index) => ({
                          "S.No.": `${__index + paginationModel.pageSize * paginationModel.page + 1}.`,
                          CreatedAt: Moment(item?.createdAt).format(
                            "YYYY-MM-DD",
                          ),
                          UpdatedAt: Moment(item?.updatedAt).format(
                            "YYYY-MM-DD",
                          ),
                          displayName: item?.displayName ?? "-",
                          VendorAccount: item.VendorAccount?.length ?? "-",
                        }),
                      )
                }
                headersData={[
                  "S.No.",
                  "CreatedAt",
                  "UpdatedAt",
                  "displayName",
                  "VendorAccount",
                ].map((i) => ({ key: i, label: i }))}
              />
            </Flex>
          </Flex>
          <DataGrid
            rows={rows}
            columns={columns}
            rowHeight={48}
            rowCount={rowCount}
            paginationMode="server"
            disableColumnSorting={true}
            filterModel={filterModel}
            onFilterModelChange={_HandleFilterModelChange}
            paginationModel={paginationModel}
            onPaginationModelChange={_HandlePaginationModelChange}
            pageSizeOptions={[10, 30, 50, 100]}
            slots={{
              loadingOverlay: () => (
                <TableSpinner
                  isLoading={true}
                  isEmpty={false}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noResultsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noRowsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={_HandleColumnVisibilityModelChange}
            loading={_QueryVendorAccounttRead?.loading}
          />
        </Flex>
        <Modal
          size="md"
          title="Add Vendor Account"
          isOpen={isVendorAccounttCreateOpen}
          onClose={onVendorAccounttCreateClose}
        >
          <VendorAccounttUpsert isCreateOnly={true} />
        </Modal>
        <Modal
          size="md"
          title="Update Vendor Account"
          isOpen={isVendorAccounttUpdateOpen}
          onClose={onVendorAccounttUpdateClose}
        >
          <VendorAccounttUpsert isCreateOnly={false} />
        </Modal>
        <Modal
          size="md"
          title="Remove Vendor Account"
          isOpen={isVendorAccounttDeleteOpen}
          onClose={onVendorAccounttDeleteClose}
        >
          <VendorAccounttDelete />
        </Modal>
      </Flex>
    </>
  );
}



/*
 * PROPTYPES
 */
Index.propTypes = {
  'passOn': PropTypes.object
}


/*
 * REDUX
 */
const _MapStateToProps = __state => ({ 'passOn': __state.PassOn })


/*
 * EXPORT
 */
export default connect(_MapStateToProps)(Index)
